import React, { useCallback, useMemo } from 'react';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import { useDropzone } from 'react-dropzone';

import { useConfigStore } from '../../store/config';
import { useAttachmentMutations } from '../../hooks/useAttachments';

import { Dropdown, OptionsType } from '../core/Dropdown';
import { typeOperators } from '../filter/FilterCard/FilterConditionOperatorInput';

import Tooltip from '../Tooltip';
import CheckBox from '../core/Checkbox';
import Input from '../core/Input';

import CreatableMultiSelect from './CreatableMultiSelect';
import useSchemaManagementDialogStore from '../../store/schemaManagementDialog';

const types = Object.keys(typeOperators)
  .filter((t) => !t.includes('array') || t === 'array:string')
  .map((t) => ({ id: t }));

const messages = defineMessages({
  name: {
    id: 'schema_management_dialog.name',
    defaultMessage: 'Name',
  },
  type: {
    id: 'schema_management_dialog.type',
    defaultMessage: 'Type',
  },
  help_text: {
    id: 'schema_management_dialog.help_text',
    defaultMessage: 'Help text',
  },
  errorReportingURL: {
    id: 'schema_management_dialog.error_reporting_url',
    defaultMessage: 'Error reporting URL',
  },
  choose: {
    id: 'choose',
    defaultMessage: 'Choose...',
  },
  operator_type_unknown: {
    id: 'schema_management_dialog.operator_type.unknown',
    defaultMessage: 'unknown',
  },
  operator_type_boolean: {
    id: 'schema_management_dialog.operator_type.boolean',
    defaultMessage: 'boolean',
  },
  operator_type_date: {
    id: 'schema_management_dialog.operator_type.date',
    defaultMessage: 'date',
  },
  operator_type_enum: {
    id: 'schema_management_dialog.operator_type.enum',
    defaultMessage: 'enum',
  },
  operator_type_number: {
    id: 'schema_management_dialog.operator_type.number',
    defaultMessage: 'number',
  },
  operator_type_photo: {
    id: 'schema_management_dialog.operator_type.photo',
    defaultMessage: 'photo',
  },
  operator_type_string: {
    id: 'schema_management_dialog.operator_type.string',
    defaultMessage: 'string',
  },
  operator_type_location: {
    id: 'schema_management_dialog.operator_type.location',
    defaultMessage: 'location',
  },
  operator_type_array_string: {
    id: 'schema_management_dialog.operator_type.array:string',
    defaultMessage: 'array:string',
  },
  operator_type_array_number: {
    id: 'schema_management_dialog.operator_type.array:number',
    defaultMessage: 'array:number',
  },
  hidden: {
    id: 'schema_management_dialog.hidden',
    defaultMessage: 'Hidden',
  },
});

const baseStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  cursor: 'pointer',
};

const activeStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

const SchemaManagementPropertyEditor: React.FC = () => {
  const intl = useIntl();
  const { attachmentUrl } = useConfigStore();
  const {
    create: { mutateAsync: createAttachment },
  } = useAttachmentMutations();

  const {
    selectedTreeNode,
    originalPropsLookup,
    propsLookup,
    setProperty,
    extrasLookup,
    setExtras,
  } = useSchemaManagementDialogStore();

  const onDrop = useCallback(
    (image: File[]) => {
      void createAttachment(image[0]).then((attachment) => {
        if (selectedTreeNode) {
          setExtras(selectedTreeNode.id, {
            help_image: attachment.id,
          });
        }
      });
    },
    [setExtras, selectedTreeNode, createAttachment],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDropAccepted: onDrop,
    accept: ['image/png', 'image/jpeg', 'image/jpg'],
    multiple: false,
  });

  const dropzoneStyle: React.CSSProperties = useMemo(() => {
    const bgImage =
      selectedTreeNode && extrasLookup[selectedTreeNode.id]?.help_image
        ? extrasLookup[selectedTreeNode.id].help_image
        : undefined;

    return bgImage
      ? {
          backgroundImage: `url(${attachmentUrl}/${bgImage}/md)`,
          ...baseStyle,
        }
      : { ...baseStyle };
  }, [attachmentUrl, extrasLookup, selectedTreeNode]);

  const style = useMemo(
    () => ({
      ...dropzoneStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
    }),
    [dropzoneStyle, isDragActive, isDragReject, isDragAccept],
  );

  const typeOptions: OptionsType[] = useMemo(() => {
    const labelMap: Record<string, string> = {
      boolean: intl.formatMessage(messages.operator_type_boolean),
      date: intl.formatMessage(messages.operator_type_date),
      enum: intl.formatMessage(messages.operator_type_enum),
      number: intl.formatMessage(messages.operator_type_number),
      photo: intl.formatMessage(messages.operator_type_photo),
      string: intl.formatMessage(messages.operator_type_string),
      location: intl.formatMessage(messages.operator_type_location),
      'array:string': intl.formatMessage(messages.operator_type_array_string),
      'array:number': intl.formatMessage(messages.operator_type_array_number),
    };
    return types.map((t) => ({
      id: t.id,
      label:
        labelMap[t.id] ?? intl.formatMessage(messages.operator_type_unknown),
    }));
  }, [intl]);

  if (!selectedTreeNode) {
    return <div className="flex grow bg-background-100" />;
  }

  return (
    <div className="flex grow bg-background-100 p-2">
      <div className="flex h-full grow flex-col p-4">
        <h5 className="text-xl font-semibold text-text-950">
          <FormattedMessage
            id="schema_management_dialog.edit_property"
            defaultMessage="Edit property"
          />
        </h5>
        <div className="flex h-full flex-row pt-2">
          <div className="m-2 ml-0 flex flex-1 flex-col pr-2">
            <div className="pt-6">
              <Input
                id="schema-management-property-name"
                label={intl.formatMessage(messages.name)}
                value={propsLookup[selectedTreeNode.id]?.name || ''}
                onChange={(e) => {
                  if (selectedTreeNode) {
                    setProperty(selectedTreeNode.id, {
                      name: e.currentTarget.value,
                    });
                  }
                }}
              />
            </div>
            <div className="flex flex-col items-start pt-2">
              <div className="mr-2">
                <span className="text-[10px] uppercase leading-4 tracking-[1.5px]">
                  <FormattedMessage
                    id="schema_management_dialog.type"
                    defaultMessage="Type"
                  />
                </span>
              </div>
              <Dropdown
                id="schema-management-type-select"
                disabled={!!originalPropsLookup[selectedTreeNode.id]}
                value={typeOptions.find(
                  (t) => t.id === propsLookup[selectedTreeNode.id]?.type,
                )}
                options={typeOptions}
                placeholder={intl.formatMessage(messages.choose)}
                onValueChange={(v) => {
                  if (selectedTreeNode && v) {
                    setProperty(selectedTreeNode.id, {
                      type: v.id as ApiEntitySchemaProp['type'],
                    });
                  }
                }}
              />
            </div>
            {selectedTreeNode &&
            propsLookup[selectedTreeNode.id]?.type === 'enum' ? (
              <div className="mt-2">
                <p className="text-base font-semibold leading-6">
                  <FormattedMessage
                    id="schema_management_dialog.alternatives"
                    defaultMessage="Alternatives"
                  />
                </p>
                <CreatableMultiSelect
                  values={propsLookup[selectedTreeNode.id].alternatives || []}
                  staticValues={
                    originalPropsLookup[selectedTreeNode.id]?.alternatives || []
                  }
                  onChange={(v) => {
                    if (selectedTreeNode) {
                      setProperty(selectedTreeNode.id, { alternatives: v });
                    }
                  }}
                />
              </div>
            ) : undefined}
            <div className="flex flex-row items-center pt-6">
              <Tooltip
                label={
                  <FormattedMessage
                    id="schema_management_dialog.hidden_properties_explanation"
                    defaultMessage="Hidden properties are no longer shown to users"
                  />
                }
              >
                <CheckBox
                  id="schema-management-hidden"
                  aria-labelledby="schema-management-hidden-label"
                  checked={extrasLookup[selectedTreeNode.id]?.hidden || false}
                  label={intl.formatMessage(messages.hidden)}
                  onCheckedChange={() => {
                    if (selectedTreeNode) {
                      setExtras(selectedTreeNode.id, {
                        hidden: !(
                          extrasLookup[selectedTreeNode.id]?.hidden || false
                        ),
                      });
                    }
                  }}
                />
              </Tooltip>
            </div>
          </div>
          <div className="m-2 flex flex-1 grow flex-col">
            <div className="mb-2 pt-6">
              <Input
                id="schema-management-error-reporting-url"
                label={intl.formatMessage(messages.errorReportingURL)}
                value={
                  extrasLookup[selectedTreeNode.id]?.support_urls?.default || ''
                }
                onChange={(e) => {
                  if (selectedTreeNode) {
                    const def = { default: e.currentTarget.value };
                    setExtras(selectedTreeNode.id, { support_urls: def });
                  }
                }}
              />
            </div>
            <div className="pt-6">
              <Input
                id="schema-management-help-text"
                label={intl.formatMessage(messages.help_text)}
                value={extrasLookup[selectedTreeNode.id]?.help_text || ''}
                type="multiline"
                minRows={3}
                onChange={(e) => {
                  if (selectedTreeNode) {
                    setExtras(selectedTreeNode.id, {
                      help_text: e.currentTarget.value,
                    });
                  }
                }}
              />
            </div>
            <div className="mb-2 mt-4">
              <span className="text-[10px] uppercase leading-4 tracking-[1.5px]">
                <FormattedMessage
                  id="schema_management_dialog.help_image"
                  defaultMessage="Help image (Replace by clicking or dropping onto the image)"
                />
              </span>
            </div>
            <div {...getRootProps({ style })} className="mt-2">
              <input {...getInputProps()} />
              {!extrasLookup[selectedTreeNode.id]?.help_image ? (
                isDragActive ? (
                  <p>
                    <FormattedMessage
                      id="schema_management_dialog.drop_image_here"
                      defaultMessage="Drop replacement image here..."
                    />
                  </p>
                ) : (
                  <p>
                    <FormattedMessage
                      id="schema_management_dialog.drag_or_click"
                      defaultMessage="Drag a replacement image here, or click to select one"
                    />
                  </p>
                )
              ) : undefined}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchemaManagementPropertyEditor;
